<script setup>
import { usePhotoService } from '@/composables/usePhotoService';
import { ref } from 'vue';
import EmbedVideoForm from '../EmbedVideoForm.vue';
import PhotoGalleryForm from '../PhotoGalleryForm.vue';
import BaseModal from './BaseModal.vue';

const { processPhoto, fileName, imgObj, fileType, resetPhoto, fileSize } = usePhotoService();
const props = defineProps(['open', 'pageLink', 'photos']);
const emit = defineEmits(['close', 'update-video', 'update-images']);
const activeAsset = ref({});

onMounted(() => {
  const events = ['dragenter', 'dragleave', 'dragover', 'drop'];
  events.forEach((event) => document.body.addEventListener(event, (e) => e.preventDefault()));
});

onUnmounted(() => {
  const events = ['dragenter', 'dragleave', 'dragover', 'drop'];
  events.forEach((event) => document.body.removeEventListener(event, (e) => e.preventDefault()));
});

const mediaSelection = ref('');
</script>
<template>
  <BaseModal
    :modal-open="open"
    @close="emit('close')"
    backgroundHeader="transparent"
    max-width="700px"
  >
    <template v-slot:header>
      <v-row justify="space-between" class="py-3 px-2">
        <h3 style="color: var(--fundeasy-colors-primary-900)">Media Manager</h3>
        <v-btn @click="emit('close')" icon="mdi-close" variant="text" size="small" />
      </v-row>
    </template>
    <template v-slot:default>
      <v-row>
        <v-col>
          <p style="font-weight: bold; font-size: 14px">
            You can create a photo slideshow or embed a video on your fundraising page, it&apos;s
            easy!
          </p>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <p style="font-style: italic; font-size: 14px">
            Don&apos;t worry, you can change your mind at any time!
          </p>
        </v-col>
      </v-row>
      <v-row class="my-4">
        <v-col class="text-center">
          <v-btn-toggle divided v-model="mediaSelection">
            <v-btn
              elevation="1"
              prepend-icon="mdi-image-multiple"
              :variant="mediaSelection !== 'photos' ? 'outlined' : 'elevated'"
              :class="{ 'custom-outline': mediaSelection !== 'photos' }"
              color="success"
              value="photos"
              >Photo Gallery</v-btn
            >
            <v-btn
              elevation="1"
              prepend-icon="mdi-filmstrip"
              value="video"
              color="success"
              :class="{ 'custom-outline': mediaSelection !== 'video' }"
              :variant="mediaSelection !== 'video' ? 'outlined' : 'elevated'"
              >Embed A Video</v-btn
            >
          </v-btn-toggle>
        </v-col>
      </v-row>
      <EmbedVideoForm
        header-text="Post a video on your fundraising page to really get people’s attention."
        sub-heading-text="Go to your video on YouTube or Vimeo, then simply copy and paste the URL into the field below."
        v-if="mediaSelection === 'video'"
        @close="$emit('close')"
        @update-video="$emit('update-video', $event)"
        :videoUrl="pageLink"
      />
      <PhotoGalleryForm
        :assets="photos"
        @update-assets="$emit('update-images', $event)"
        headerText="Post a slideshow with up to 5 photos on your fundraising page to really make it stand
            out."
        v-if="mediaSelection === 'photos'"
      />
    </template>
  </BaseModal>
</template>
<style scoped>
.v-btn--variant-outlined.custom-outline:not(.v-btn--is-elevated) {
  border-color: rgb(
    var(--v-theme-success)
  ); /* Utilizing Vuetify's CSS variables for theme colors */
  color: rgb(var(--v-theme-success)); /* This sets the text color to success color too */
}
</style>
